import RootStore from '@App/stores/root-store'
import { action, makeObservable, observable } from 'mobx'
import { HistoryWorkQuery } from '@Entities/search'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { RegistrationHistoryTabName } from '@App/enum/registration-history-tab-name'
import RegistrationHistoryTabStore from '@App/stores/rightsholder/registrationHistory/registration-history-tab-store'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { generateSearchCriterionFromOption } from '@App/helpers/generate-search-criterion'
import { RegistrationHistoryFilter } from '@App/forms/filters/reg-history-filter'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { v4 as uuidv4 } from 'uuid'
import getWorkAutoCompleteSuggestions from '@App/helpers/search/get-work-autocomplete-suggestions'
import getWriterNameAutoCompleteSuggestions from '@App/helpers/search/get-writer-name-auto-complete-suggestions'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'

const SEARCH_REG_HISTORY_WORK_OPTIONS: Array<SelectOptionHide<HistoryWorkQuery>> = [
    {
        id: uuidv4(),
        value: 'title',
        label: 'Work Title',
        isPrimary: true,
        autoCompleteFetcher: getWorkAutoCompleteSuggestions,
    },
    {
        id: uuidv4(),
        value: 'writerName',
        label: 'Writer Name',
        autoCompleteFetcher: getWriterNameAutoCompleteSuggestions,
    },
    { id: uuidv4(), value: 'writerIpi', label: 'Writer IPI' },
    { id: uuidv4(), value: 'publisherName', label: 'Publisher Name' },
    { id: uuidv4(), value: 'publisherIpi', label: 'Publisher IPI' },
    { id: uuidv4(), value: 'recordingName', label: 'Recording Title' },
    { id: uuidv4(), value: 'artist', label: 'Artist' },
    { id: uuidv4(), value: 'isrc', label: 'ISRC' },
]
export const defaultRegHistorySelectionCriteria: Array<SearchCriterion<HistoryWorkQuery>> = [
    generateSearchCriterionFromOption(SEARCH_REG_HISTORY_WORK_OPTIONS[0], true),
]

export const getDefaultRegHistoryFilters = (): RegistrationHistoryFilter => ({
    sort_by: 'last_update_date',
})

export default class RegistrationHistoryStore {
    private readonly _registrationHistoryTabs: Map<RegistrationHistoryTabName, RegistrationHistoryTabStore>
    private _criteriaStore: SearchCriteriaStore<HistoryWorkQuery>
    private _currentQuery: HistoryWorkQuery
    private _currentFilters: RegistrationHistoryFilter = getDefaultRegHistoryFilters()

    constructor(rootStore: RootStore) {
        makeObservable<
            RegistrationHistoryStore,
            '_criteriaStore' | '_registrationHistoryTabs' | '_currentQuery' | '_currentFilters'
        >(this, {
            _criteriaStore: observable,
            _registrationHistoryTabs: observable,
            _currentQuery: observable,
            _currentFilters: observable,
            resetRegHistory: action,
            setCurrentQuery: action,
            seedSearchFromUrl: action,
            setCurrentFilters: action,
        })

        this._currentQuery = {}
        this._criteriaStore = this.createRegHistoryCriteriaStore()
        this._registrationHistoryTabs = this.createRegistrationHistoryTabs(rootStore)
    }

    get criteriaStore(): SearchCriteriaStore<HistoryWorkQuery> {
        return this._criteriaStore
    }

    get currentFilters() {
        return this._currentFilters
    }

    setCurrentFilters(filters: RegistrationHistoryFilter): void {
        this._currentFilters = filters
    }

    get tabNames(): RegistrationHistoryTabName[] {
        return (Object.keys(RegistrationHistoryTabName) as Array<keyof typeof RegistrationHistoryTabName>).map(
            (key) => RegistrationHistoryTabName[key]
        )
    }

    get registrationHistoryTabs(): Map<RegistrationHistoryTabName, RegistrationHistoryTabStore> {
        return this._registrationHistoryTabs
    }

    get currentQuery(): HistoryWorkQuery {
        return this._currentQuery
    }

    setCurrentQuery(query: HistoryWorkQuery) {
        this._currentQuery = query
    }

    /**
     * Starts a visit from a link's search terms. Run it before the tab first renders: it swaps in a criteria
     * store seeded from the URL and — only if the terms satisfy the form's own "ready to search" rule — makes
     * them the search, so the first request already carries them. Results already held are only reused when
     * they were fetched under the same query (see `RegistrationHistoryTabStore.lastResultsScope`).
     */
    seedSearchFromUrl(criteria: Record<string, unknown>): void {
        this._criteriaStore = this.createRegHistoryCriteriaStore()
        this._criteriaStore.applyCriteriaFromObject(criteria)
        const rows = this._criteriaStore.searchCriteria
        this._currentQuery = this._criteriaStore.isCriteriaSearchReady(rows) ? mapCriteriaToObject(rows) : {}
    }

    resetRegHistory(): void {
        this._currentQuery = {}
        this._currentFilters = getDefaultRegHistoryFilters()
        this._criteriaStore = this.createRegHistoryCriteriaStore()
        this._registrationHistoryTabs.forEach((tab) => tab.resetStore())
    }

    private createRegHistoryCriteriaStore(): SearchCriteriaStore<HistoryWorkQuery> {
        const maxCriteria = 8
        return new SearchCriteriaStore(
            defaultRegHistorySelectionCriteria,
            SEARCH_REG_HISTORY_WORK_OPTIONS,
            SearchOptionType.primary,
            RequiredCriteria.first,
            maxCriteria
        )
    }

    isCriteriaPresent(): boolean {
        return Object.values(this._criteriaStore.searchCriteria).some(
            (criteria) => !!criteria.searchTerm && criteria.searchTerm !== ''
        )
    }

    private createRegistrationHistoryTabs(
        rootStore: RootStore
    ): Map<RegistrationHistoryTabName, RegistrationHistoryTabStore> {
        return new Map(this.tabNames.map((tab) => [tab, new RegistrationHistoryTabStore(rootStore)]))
    }
}
